import { z } from 'zod';
import { ACTIVITY_TYPES } from '../../src/domain/activity/activityType';
import { PERMISSIONS, ROLES, VIEW_KEYS } from '../../src/domain/access/role';
import { CARRIERS, SHADES } from '../../src/domain/catalog/catalog';
import { STAGE_ORDER } from '../../src/domain/kit/stage';
import { DELETION_STATUSES } from '../../src/domain/order/deletion';
import { ORDER_STATES } from '../../src/domain/order/orderState';
import { ORIGINS } from '../../src/domain/order/origin';
import { SHIPPING_METHODS, SHIPPING_STATUSES } from '../../src/domain/order/shipping';

/**
 * The zod schemas are the single source of truth, section 6. From them come the
 * TypeScript types, `contracts/openapi.yaml` for the backend developer, the MSW
 * response validation and the runtime parsing in the development HTTP client.
 *
 * These files import the enumerations from `src/domain` rather than restating
 * them, so there is exactly one definition of the string that travels on the
 * wire. They import nothing else, and in particular they do not import
 * `@asteasolutions/zod-to-openapi`: the OpenAPI metadata is attached in
 * `contracts/openapi.ts`, which is a build script and never reaches the bundle.
 */

/** z.enum wants a non-empty tuple; the domain exports readonly arrays. */
function enumOf<T extends string>(values: readonly T[]) {
  return z.enum(values as unknown as [T, ...T[]]);
}

export const StageSchema = enumOf(STAGE_ORDER);
export const InputTypeSchema = z.enum(['digital', 'impression']);
export const RoleSchema = enumOf(ROLES);
export const PermissionSchema = enumOf(PERMISSIONS);
export const ViewKeySchema = enumOf(VIEW_KEYS);
export const OrderStateSchema = enumOf(ORDER_STATES);
export const DeletionStatusSchema = enumOf(DELETION_STATUSES);
export const ShippingMethodSchema = enumOf(SHIPPING_METHODS);
export const ShippingStatusSchema = enumOf(SHIPPING_STATUSES);
export const ActivityTypeSchema = enumOf(ACTIVITY_TYPES);
export const ShadeSchema = enumOf(SHADES);
/** Where a case comes from, in place of its country. Section 1 of the request of 24 September 2026. */
export const OriginSchema = enumOf(ORIGINS);
export const CarrierSchema = enumOf(CARRIERS);

/**
 * Instants are UTC with a Z suffix. Calendar dates have no time component at
 * all. Section 7.1. The two are kept apart on purpose: `startDate` is a date the
 * lab agreed with a dentist, not a moment, and giving it a time zone is how a
 * delivery silently moves a day when the server is not in Dubai.
 */
export const InstantSchema = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/,
    'must be an ISO 8601 instant in UTC, ending Z',
  );

export const CalendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a calendar date, YYYY-MM-DD, with no time component');

/** An FDI permanent tooth number as a string: "11" to "48". */
export const ToothNumberSchema = z
  .string()
  .regex(/^[1-4][1-8]$/, 'must be an FDI tooth number');

/** IDs are strings everywhere, even where the database uses an integer or a GUID. */
export const IdSchema = z.string().min(1);
export const KitIdSchema = z.string().regex(/^K-\d+$/, 'must look like K-1084');
export const JobOrderNoSchema = z.string().regex(/^JO-\d+$/, 'must look like JO-2041');
export const QrCodeSchema = z.string().min(1);

/** Section 7.9. Sent back in an If-Match header on every mutation. */
export const RowVersionSchema = z.string().min(1);

/**
 * Money is a decimal number in dirhams (AED) with two places: the system's
 * currency since the lab's request of 17 October 2026, item 13. Dollars are
 * only ever shown, at the rate Accounts sets (`GET /finance/settings`).
 */
export const MoneySchema = z.number().nonnegative();

/**
 * Fields the server OMITS rather than blanks, section 7.10 items 1 and 2. The
 * schema accepts an absent key and a null so that one backend quirk does not
 * take the floor down, but the mock emits them absent and a test asserts it.
 */
export const OmittedIdentitySchema = z.string().nullish();
export const OmittedMoneySchema = MoneySchema.nullish();

/**
 * The id of a doctor's or a patient's record, on a payload that carries the
 * two names. Omitted exactly where they are, for a caller without seeIdentity,
 * and null on a case that was never linked to a record. The request of
 * 6 October 2026 (second), items 4 and 5.
 */
export const OmittedIdentityIdSchema = IdSchema.nullish();

/**
 * A case released by Accounts to be shipped before it is paid: when, and who
 * did it, by the name they carry. The lab's request of 17 October 2026, item 8.
 */
export const CaseReleaseSchema = z.object({
  at: InstantSchema,
  byName: z.string().nullable(),
});

/** Section 7.1. Every list response is wrapped. */
export function pageOf<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive().max(200),
    total: z.number().int().nonnegative(),
  });
}

export type Page<T> = { items: T[]; page: number; pageSize: number; total: number };

/** Query parameters accepted by every list endpoint. */
export const ListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(200).default(50),
  sort: z.string().optional(),
  q: z.string().optional(),
});
export type ListQuery = z.infer<typeof ListQuerySchema>;

/**
 * RFC 7807, which is the ASP.NET Core default. `traceId` is on every response
 * so a screenshot from the lab floor is diagnosable; the UI renders it in a
 * small monospace line.
 */
export const ProblemDetailsSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  traceId: z.string().optional(),
  errors: z.record(z.string(), z.array(z.string())).optional(),
});
export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;

/** Every one of the four work commands carries one, section 7.4. */
export const IdempotencyKeySchema = z.string().uuid();
